/* eslint-disable @typescript-eslint/no-unused-vars */

import "amazon-connect-streams";
import React, { FC, PropsWithChildren, useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { ConnectDataContext } from "./DataContext";
import { CallContext } from "./CallContext";
import { IAgent, ICall, IQueue, IUser, IPhoneDirItem } from "../interfaces";
import AppContext from "./AppContextProvider";
import { remove } from "lodash";
import TokenContext from "./TokenContext";
import RestApiContext from "./RestApiContext";
import { useInterval } from "usehooks-ts";
import { getLogger } from "../services/LoggingService";
const logger = getLogger("DataContextProvider");

const ConnectDataContextProvider: FC<PropsWithChildren<any>> = (props: PropsWithChildren<any>) => {
    const idRegex: RegExp = new RegExp(/\((\w+)\)/);

    const [callLog, setCallLog] = useState<ICall[]>([]);
    const [queues, setQueues] = useState<IQueue[]>([]);
    const [agents, setAgents] = useState<IAgent[]>([]);
    const [phoneDirectoryItems, setPhoneDirectoryItems] = useState<IPhoneDirItem[]>([]);
    const [fullPhoneDirectory, setFullPhoneDirectory] = useState<IPhoneDirItem[]>([]);
    const [directorySearchTerm, setDirectorySearchTerm] = useState<string>("");
    // const [phoneDirectoryConfig, setPhoneDirectorConfig] = useState<IDirectoryConfiguration>({});
    // const [dispositionConfig, setDispositionConfig] = useState<IDispositionConfiguration>({});
    const [availableDispositions, setAvailableDispositions] = useState<string[]>([]);
    // const [currentClientIdentifier, setCurrentClientIdentifier] = useState<string>("");
    const [currentQueueName, setCurrentQueueName] = useState<string>("");
    const [selectedQueueName, setSelectedQueueName] = useState<string>("");
    const [showDispositions, setShowDispositions] = useState<boolean>(false);

    const [contactId, setContactId] = useState<string>("");
    const [onACW, setOnACW] = useState<boolean>(false);
    const [defaultQueueName, setDefaultQueueName] = useState<string>("");
    const [availableAgentEndpoints, setAvailableAgentEndpoints] = useState<connect.Endpoint[]>([]);
    const [availableQueueEndpoints, setAvailableQueueEndpoints] = useState<connect.Endpoint[]>([]);

    const [user, setUser] = useState<IUser>({
        name: "",
        username: "",
        routingProfile: "",
        queues: [],
        initialized: false,
    });

    const appContext = useContext(AppContext);
    const { token } = useContext(TokenContext);
    const api = useContext(RestApiContext);
    const { agent, contact } = useContext(CallContext);

    const uiBlockerID = "block-clear-contact";
    const ws = useRef<WebSocket>();

    useEffect(() => {
        handleAgentChange(agent);
    }, [agent]);

    useEffect(() => {
        handleContactChange(contact);
    }, [contact]);

    useEffect(() => {
        // Start socket when username and token is available
        if (!user.username || !token) return;

        startWSS(user.username, token);
        return () => {
            ws.current.close();
            logger.debug("Closed connection");
        };
    }, [user.username, token]);

    /**
     * Attempt to reconnect socket if connection is lost
     */
    useInterval(() => {
        if (
            user.username &&
            token &&
            ws.current &&
            ![ws.current.CONNECTING, ws.current.OPEN].includes(ws.current.readyState)
        ) {
            startWSS(user.username, token);
        }
    }, 5 * 1000);

    useEffect(() => {
        if (availableDispositions && availableDispositions.length > 0 && onACW) {
            setShowDispositions(true);
        } else {
            setShowDispositions(false);
        }
    }, [availableDispositions, onACW]);

    const fetchPhoneDirectory = useCallback(async () => {
        const clientIdentifier = getClientIdentifierByQueue(currentQueueName);
        if (!clientIdentifier) {
            setFullPhoneDirectory([]);
        } else {
            try {
                const dir = await api.getTenantDirectoryByQueue(clientIdentifier, currentQueueName);
                setFullPhoneDirectory(dir);
            } catch (err) {
                setFullPhoneDirectory([]);
            }
        }
    }, [currentQueueName, token]);
    const fetchPhoneDirectoryForSelect = useCallback(async () => {
        const qName = selectedQueueName ? selectedQueueName : currentQueueName;
        const clientIdentifier = getClientIdentifierByQueue(qName);
        if (!clientIdentifier) {
            setFullPhoneDirectory([]);
        } else {
            try {
                const dir = await api.getTenantDirectoryByQueue(clientIdentifier, qName);
                setFullPhoneDirectory(dir);
            } catch (err) {
                setFullPhoneDirectory([]);
            }
        }
    }, [selectedQueueName, token]);

    const fetchDispositions = useCallback(async () => {
        const clientIdentifier = getClientIdentifierByQueue(currentQueueName);
        if (!clientIdentifier) {
            setAvailableDispositions([]);
        } else {
            try {
                const dis = await api.getTenantDispositionsByQueue(clientIdentifier, currentQueueName);
                setAvailableDispositions(dis);
            } catch (err) {
                setAvailableDispositions([]);
            }
        }
    }, [currentQueueName, token]);

    useEffect(() => {
        fetchPhoneDirectory();
    }, [fetchPhoneDirectory]);

    useEffect(() => {
        fetchPhoneDirectoryForSelect();
    }, [fetchPhoneDirectoryForSelect]);

    useEffect(() => {
        if (!directorySearchTerm) {
            if (fullPhoneDirectory.length < 500) {
                setPhoneDirectoryItems(fullPhoneDirectory);
            } else {
                setPhoneDirectoryItems([]);
            }
        } else {
            const search = directorySearchTerm.toLowerCase();
            const filtered = fullPhoneDirectory.filter((item: any) => {
                return (
                    item.name.toLowerCase().includes(search) ||
                    item.ani.toLowerCase().includes(search) ||
                    (item.ext && item.ext.toLowerCase().includes(search))
                );
            });
            setPhoneDirectoryItems(filtered);
        }
    }, [directorySearchTerm, fullPhoneDirectory]);
    useEffect(() => {
        fetchDispositions();
    }, [fetchDispositions]);

    const getQueues = useMemo(() => {
        return queues;
    }, [queues]);
    const startWSS = (username: string, token: string) => {
        logger.debug("Starting WSS");
        const subscribe = { action: "init" };
        const params = {
            AgentUsername: username,
            token,
        };
        try {
            ws.current = new WebSocket([appContext.config.wssUrl, new URLSearchParams(params).toString()].join("?"));
            ws.current.onopen = () => {
                ws.current.send(JSON.stringify(subscribe));
                logger.debug("Subscribing to WSS");
            };

            ws.current.onclose = () => {
                logger.debug("WSS has lost connection.");
                ws.current.close();
            };

            ws.current.onerror = (e) => {
                logger.debug("There was an error", e);
            };
            (window as any).WBS = ws.current;
        } catch (err) {
            console.error(
                "Could not start websocket: " +
                    [appContext.config.wssUrl, new URLSearchParams(params).toString()].join("?")
            );
            console.error("This connection will not start until a token is passed from a valid incoming call.");
            console.error(err);
        }
    };

    useEffect(() => {
        if (!ws.current) return;
        ws.current.onmessage = (event: MessageEvent) => {
            const response = JSON.parse(event.data);
            logger.debug("WSS RESPONSE", response);
            handleResponse(response);
        };
    }, [ws.current, queues, agents, user, callLog]);
    const handleResponse = (response: MessageEvent["data"]) => {
        //console.log("New Socket", response);
        switch (response.Table) {
            case "QueueMetrics":
                handleQueues(response);
                break;
            case "AgentMetrics":
                handleAgents(response);
                break;
            case "AgentCallLog":
                handleCallLog(response);
                break;
            case "PhoneDirectory":
                handlePhoneDirectory(response);
                break;
            case "DispositionConfiguration":
                handleDispositions(response);
                break;
            default:
                logger.debug("Unrecognized message from websocket", response);
        }
    };

    const handlePhoneDirectory = (response: MessageEvent["data"]) => {
        if (response.init) {
            fetchPhoneDirectory();
        } else if (response.update) {
            fetchPhoneDirectory();
        }
    };

    const handleDispositions = (response: MessageEvent["data"]) => {
        if (response.init) {
            return;
        } else if (response.update) {
            fetchDispositions();
        }
    };
    const handleQueues = (response: MessageEvent["data"]) => {
        //console.log("Queues Init", response);
        if (response.init) {
            setQueues(response.Items.filter(isRelevantQueue));

            return;
        }

        if (isRelevantQueue(response)) {
            console.log("We have relevant queues");
            console.log("Queues ", queues);
            // replace queue in current array, if present
            const index = queues.findIndex((queue: IQueue) => queue.Queue === response.Queue);
            if (index === -1) {
                setQueues([response, ...queues]);
                return;
            }
            setQueues(replaceAndReturn(queues, index, response));
        }
    };

    const handleAgents = (response: MessageEvent["data"]) => {
        if (response.init) {
            !user.initialized
                ? initializeActiveAgent(response.Items)
                : setAgents(noOfflineAgents(response.Items.filter((a: IAgent) => a.AgentUsername !== user.username)));
            return;
        }
        let newAgentsState: IAgent[];
        // replace agent in current array, if present
        if (response.AgentUsername === user.username) {
            return;
        }
        const index = agents.findIndex((agent: IAgent) => agent.AgentUsername === response.AgentUsername);

        if (index === -1) {
            newAgentsState = [response, ...agents];
        } else {
            newAgentsState = replaceAndReturn(agents, index, response);
        }

        setAgents(noOfflineAgents(newAgentsState));
    };
    const replaceAndReturn = (array: any[], index: number, newValue: any): any[] => {
        const newArray = array.slice();
        newArray.splice(index, 1, newValue);
        return newArray;
    };

    const noOfflineAgents = (agents: IAgent[]): IAgent[] => {
        return agents.filter((agent: IAgent) => isOnlineAgent(agent));
    };

    const handleCallLog = (response: MessageEvent["data"]) => {
        if (response.init) {
            setCallLog(dateSort(response.Items));
            return;
        }

        setCallLog([response, ...callLog]);
    };
    const dateSort = (calls: ICall[]): ICall[] => {
        return calls.slice().sort((callA: ICall, callB: ICall): number => {
            return new Date(callA.ConnectedTimestamp) < new Date(callB.ConnectedTimestamp) ? 1 : -1;
        });
    };

    const isRelevantQueue = (queue: IQueue) => {
        logger.debug("USER ", user, queue);
        return user.queues.includes(queue.Queue);
    };

    const isOnlineAgent = (agent: IAgent) => {
        return agent.Status !== "Offline";
    };
    const initializeActiveAgent = (agents: IAgent[]) => {
        const me = remove(agents, (agent: IAgent) => agent.AgentUsername === user.username)[0];
        if (me) {
            setUser({
                ...user,
                initialized: true,
                HierarchyLevel1: me.HierarchyLevel1,
                HierarchyLevel2: me.HierarchyLevel2,
                HierarchyLevel3: me.HierarchyLevel3,
            });
        }

        agents.forEach((agent: IAgent) => {
            setAgents(noOfflineAgents(agents));
        });
    };

    const getAgentDepartment = (agent: IAgent): string => {
        return agent.HierarchyLevel1;
    };
    const handleAgentChange = (agent: connect.Agent | undefined) => {
        if (!agent) return;
        agent.getEndpoints(agent.getAllQueueARNs(), {
            success: (data) => {
                const eps_agent = data.endpoints.filter((ep) => ep.type === "agent");
                const eps_queue = data.endpoints.filter((ep) => ep.type === "queue");
                setAvailableAgentEndpoints(eps_agent);
                setAvailableQueueEndpoints(eps_queue);
            },
            failure: function () {},
        });
        const agentConfig = agent.getConfiguration();
        const userAgentDetails: IUser = {
            name: agentConfig.name,
            username: agentConfig.username,
            routingProfile: agentConfig.routingProfile.name,
            queues: agentConfig.routingProfile.queues.map((queue) => queue.name),
        };
        setUser(userAgentDetails);
        const {
            defaultOutboundQueue: { name },
        } = agent.getRoutingProfile();
        if (defaultQueueName !== name) {
            setDefaultQueueName(name);
            if (!currentQueueName) {
                setCurrentQueueName(name);
            }
        }
        // setDefaultClientIdentifierByRoutingProfileQueue(name)
        agent.onAfterCallWork((agent) => {
            (window as any).document.getElementById(uiBlockerID).style = undefined;
            setTimeout(() => {
                (window as any).document.getElementById(uiBlockerID).style.display = "none";
            }, 3000);
        });
    };
    const getClientIdentifierByQueue = (name: string) => {
        const match = name.match(idRegex);
        if (match && match.length > 0) {
            const clientIdentifier = match[1];
            return clientIdentifier;
        } else {
            return "";
        }
    };
    const handleContactChange = (contact: connect.Contact | undefined) => {
        if (!contact) return;
        if (contact.getStatus().type === "ended") {
            // This event is fired when page is refreshed in ACW state
            const newContactId = contact.getInitialContactId();
            setContactId(newContactId);
            //setClientIdentifierByQueue(contact);
            const currentQueue = contact.getQueue();
            if (currentQueue.name !== currentQueueName) {
                setCurrentQueueName(currentQueue.name);
            }
            if (token) {
                api.updateContactAttributes(contact.getInitialContactId(), {
                    HierarchyLevel1: user.HierarchyLevel1 ? user.HierarchyLevel1 : "",
                    HierarchyLevel2: user.HierarchyLevel2 ? user.HierarchyLevel2 : "",
                    HierarchyLevel3: user.HierarchyLevel3 ? user.HierarchyLevel3 : "",
                });
            }
        }
        contact.onConnecting(() => {
            logger.debug("CONTACT:CONNECTING");
        });
        contact.onMissed(() => {
            logger.debug("CONTACT:MISSED");
        });
        contact.onConnected(() => {
            const newContactId = contact.getInitialContactId();
            setContactId(newContactId);
            //setClientIdentifierByQueue(contact);
            const currentQueue = contact.getQueue();
            if (currentQueue.name !== currentQueueName) {
                setCurrentQueueName(currentQueue.name);
            }
            if (token) {
                api.updateContactAttributes(contact.getInitialContactId(), {
                    HierarchyLevel1: user.HierarchyLevel1 ? user.HierarchyLevel1 : "",
                    HierarchyLevel2: user.HierarchyLevel2 ? user.HierarchyLevel2 : "",
                    HierarchyLevel3: user.HierarchyLevel3 ? user.HierarchyLevel3 : "",
                });
            }
        });

        contact.onACW(() => {
            if (token) {
                api.updateContactAttributes(contact.getInitialContactId(), {
                    HierarchyLevel1: user.HierarchyLevel1 ? user.HierarchyLevel1 : "",
                    HierarchyLevel2: user.HierarchyLevel2 ? user.HierarchyLevel2 : "",
                    HierarchyLevel3: user.HierarchyLevel3 ? user.HierarchyLevel3 : "",
                });
            }

            setOnACW(true);
        });

        contact.onDestroy(() => {
            setCurrentQueueName(defaultQueueName);
            setOnACW(false);
        });
    };
    return (
        <ConnectDataContext.Provider
            value={{
                agents,
                availableAgentEndpoints,
                availableQueueEndpoints,
                callLog,
                queues,
                phoneDirectoryItems,
                currentQueueName,
                selectedQueueName,
                setSelectedQueueName,
                setDirectorySearchTerm,
                availableDispositions,
                showDispositions,
                contactId,
            }}
        >
            {props.children}
        </ConnectDataContext.Provider>
    );
};

// used in dev tools
export { ConnectDataContextProvider };
